import { AnvaFlowsInsuranceTag } from '@flows/src/api/anva-flows/getAnvaFlowsInsuranceTags';
import { create } from 'zustand';

interface FlowsConfigureState {
  insurances: AnvaFlowsInsuranceTag[];
  coverages: Record<string, string[]>;
  companies: Record<string, string[]>;

  // Actions
  setInsurances: (insurances: AnvaFlowsInsuranceTag[]) => void;

  // Coverage actions
  setCoverages: (insuranceId: string, coverages: string[]) => void;
  addCoverage: (insuranceId: string, coverage: string) => void;
  removeCoverage: (insuranceId: string, coverage: string) => void;

  // Company actions
  setCompanies: (insuranceId: string, companies: string[]) => void;
  addCompany: (insuranceId: string, company: string) => void;
  removeCompany: (insuranceId: string, company: string) => void;

  // Reset
  resetStore: () => void;
}

export const useAnvaFlowsConfigureStore = create<FlowsConfigureState>((set) => {
  // Helper function to update array items in records
  const updateRecord = <T>(
    record: Record<string, T[]>,
    key: string,
    items: T[],
  ): Record<string, T[]> => ({
    ...record,
    [key]: items,
  });

  // Helper for adding an item if it doesn't already exist
  const addItem = <T>(
    record: Record<string, T[]>,
    key: string,
    item: T,
  ): Record<string, T[]> => {
    const existingItems = record[key] || [];
    if (existingItems.includes(item)) return record;

    return updateRecord(record, key, [...existingItems, item]);
  };

  // Helper for removing an item
  const removeItem = <T>(
    record: Record<string, T[]>,
    key: string,
    item: T,
  ): Record<string, T[]> => {
    const existingItems = record[key] || [];
    if (!existingItems.includes(item)) return record;

    return updateRecord(
      record,
      key,
      existingItems.filter((i) => i !== item),
    );
  };

  return {
    insurances: [],
    coverages: {},
    companies: {},

    // Insurances
    setInsurances: (insurances) => set({ insurances }),

    // Coverages
    setCoverages: (insuranceId, coverages) =>
      set((state) => ({
        coverages: updateRecord(state.coverages, insuranceId, coverages),
      })),

    addCoverage: (insuranceId, coverage) =>
      set((state) => ({
        coverages: addItem(state.coverages, insuranceId, coverage),
      })),

    removeCoverage: (insuranceId, coverage) =>
      set((state) => ({
        coverages: removeItem(state.coverages, insuranceId, coverage),
      })),

    // Companies
    setCompanies: (insuranceId, companies) =>
      set((state) => ({
        companies: updateRecord(state.companies, insuranceId, companies),
      })),

    addCompany: (insuranceId, company) =>
      set((state) => ({
        companies: addItem(state.companies, insuranceId, company),
      })),

    removeCompany: (insuranceId, company) =>
      set((state) => ({
        companies: removeItem(state.companies, insuranceId, company),
      })),

    // Reset
    resetStore: () => set({ insurances: [], coverages: {}, companies: {} }),
  };
});
